import React, {useEffect, useState} from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import ErrorBanner from '@/Components/ErrorBanner';
import {Colors} from '@/styles';
import {
  DEFAULT_ROYALE_SETTINGS,
  DIFFICULTY_MOVE_LABELS,
  DIFFICULTY_OPTIONS,
  MAX_TOPICS,
  PUNISH_THRESHOLDS,
  TIME_CHIPS,
  type Difficulty,
} from '@/Screens/FunChallenges/royaleConstants';
import {
  ROYALE_TOPICS,
  TOPIC_LABELS,
} from '@/Screens/FunChallenges/royaleTopics';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';

const DURATION_LABELS: Record<number, {name: string; time: string}> = {
  30: {name: 'Quick', time: '30 sec'},
  60: {name: 'Sprint', time: '1 min'},
  90: {name: 'Classic', time: '1.5 min'},
  120: {name: 'Dash', time: '2 min'},
  180: {name: 'Endurance', time: '3 min'},
  300: {name: 'Marathon', time: '5 min'},
};

const DIFFICULTY_COPY: Record<
  Difficulty,
  {icon: string; title: string; description: string}
> = {
  easy: {
    icon: '🌱',
    title: 'Easy',
    description: 'Fast, friendly puzzles',
  },
  medium: {
    icon: '⚡',
    title: 'Medium',
    description: 'A balanced challenge',
  },
  hard: {
    icon: '🔥',
    title: 'Hard',
    description: 'For serious competitors',
  },
};

const freshSettings = (): RoyaleSettingsValue => ({
  ...DEFAULT_ROYALE_SETTINGS,
  topics: [],
});

export default function ClubFunChallengeSheet({
  visible,
  clubName,
  submitting,
  created,
  error,
  onClose,
  onCreate,
}: {
  visible: boolean;
  clubName: string;
  submitting?: boolean;
  created?: boolean;
  error?: string;
  onClose: () => void;
  onCreate: (settings: RoyaleSettingsValue) => void;
}) {
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;
  const [settings, setSettings] = useState<RoyaleSettingsValue>(freshSettings);
  const [topicPickerOpen, setTopicPickerOpen] = useState(false);
  const [topicDraft, setTopicDraft] = useState<string[]>([]);
  const [topicSearch, setTopicSearch] = useState('');

  useEffect(() => {
    if (!visible) {
      setSettings(freshSettings());
      setTopicPickerOpen(false);
      setTopicSearch('');
    }
  }, [visible]);

  const close = () => {
    setSettings(freshSettings());
    setTopicPickerOpen(false);
    setTopicSearch('');
    onClose();
  };

  const submit = () => {
    if (!submitting) {
      onCreate(settings);
    }
  };

  const update = (next: Partial<RoyaleSettingsValue>) =>
    setSettings(current => ({...current, ...next}));

  const openTopicPicker = () => {
    setTopicDraft(settings.topics);
    setTopicSearch('');
    setTopicPickerOpen(true);
  };

  const toggleTopic = (topic: string) => {
    setTopicDraft(current => {
      if (current.includes(topic)) {
        return current.filter(item => item !== topic);
      }
      if (current.length >= MAX_TOPICS) {
        return current;
      }
      return [...current, topic];
    });
  };

  const closeTopicPicker = () => {
    setTopicPickerOpen(false);
    setTopicSearch('');
  };

  const topicSummary =
    settings.topics.length === 0
      ? 'Any topic'
      : `${settings.topics.length} topic${
          settings.topics.length === 1 ? '' : 's'
        }`;
  const duration =
    DURATION_LABELS[settings.timerSec]?.time ?? `${settings.timerSec}s`;
  const mistakeSummary = settings.punish
    ? `Out after ${settings.punishThreshold ?? 5} mistakes`
    : 'Everyone finishes';

  if (!visible) {
    return null;
  }

  return (
    <>
      <Modal
        transparent
        animationType={isWide ? 'fade' : 'slide'}
        visible={visible}
        onRequestClose={topicPickerOpen ? closeTopicPicker : close}>
        <Pressable
          testID="fun-challenge-backdrop"
          style={[styles.backdrop, isWide ? styles.center : styles.bottom]}
          onPress={close}>
          <Pressable
            testID="club-fun-challenge-sheet"
            style={[styles.sheet, isWide ? styles.card : styles.bottomSheet]}
            onPress={() => {}}>
            {!isWide ? <View style={styles.grabber} /> : null}

            <View style={styles.hero}>
              <View style={styles.heroTopRow}>
                <View style={styles.heroIcon}>
                  <CCText style={styles.heroIconText}>♛</CCText>
                </View>
                <Pressable
                  testID="fun-challenge-close-btn"
                  accessibilityLabel="Close"
                  onPress={close}
                  style={styles.closeButton}>
                  <CCText style={styles.closeButtonText}>×</CCText>
                </Pressable>
              </View>
              <CCText
                fontWeight="thunder"
                style={[styles.title, !isWide && styles.titleMobile]}>
                Create Puzzle Royal
              </CCText>
              <CCText style={styles.subtitle}>
                Set up a live Puzzle Royal for {clubName}. Members can jump in
                directly from club chat.
              </CCText>
            </View>

            <ScrollView
              testID="fun-challenge-scroll"
              style={styles.scroll}
              contentContainerStyle={styles.content}
              showsVerticalScrollIndicator
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              alwaysBounceVertical={false}>
              <View style={styles.infoStrip}>
                <CCText style={styles.infoIcon}>👥</CCText>
                <View style={styles.infoCopy}>
                  <CCText style={styles.infoTitle}>How it works</CCText>
                  <CCText style={styles.infoText}>
                    Create the lobby, wait for at least 2 players, then start
                    the race. The highest score wins.
                  </CCText>
                </View>
              </View>

              <Section
                step="1"
                title="Choose the race length"
                hint="How long should players solve?">
                <View style={styles.durationGrid}>
                  {TIME_CHIPS.map(time => {
                    const selected = settings.timerSec === time;
                    const label = DURATION_LABELS[time] ?? {
                      name: `${time}s`,
                      time: '',
                    };
                    return (
                      <Pressable
                        key={time}
                        testID={`fun-duration-${time}`}
                        onPress={() => update({timerSec: time})}
                        style={[
                          styles.durationCard,
                          selected && styles.choiceSelected,
                        ]}>
                        <CCText
                          style={[
                            styles.durationName,
                            selected && styles.choiceTitleSelected,
                          ]}>
                          {label.name}
                        </CCText>
                        <CCText
                          style={[
                            styles.durationTime,
                            selected && styles.choiceTextSelected,
                          ]}>
                          {label.time}
                        </CCText>
                        {selected ? (
                          <View style={styles.checkBadge}>
                            <CCText style={styles.checkText}>✓</CCText>
                          </View>
                        ) : null}
                      </Pressable>
                    );
                  })}
                </View>
              </Section>

              <Section
                step="2"
                title="Pick a difficulty"
                hint="Choose what feels right for your club.">
                <View style={styles.difficultyGrid}>
                  {DIFFICULTY_OPTIONS.map(difficulty => {
                    const selected = settings.difficulty === difficulty;
                    const copy = DIFFICULTY_COPY[difficulty];
                    return (
                      <Pressable
                        key={difficulty}
                        testID={`fun-difficulty-${difficulty}`}
                        onPress={() => update({difficulty})}
                        style={[
                          styles.difficultyCard,
                          selected && styles.choiceSelected,
                        ]}>
                        <CCText style={styles.difficultyIcon}>
                          {copy.icon}
                        </CCText>
                        <CCText
                          style={[
                            styles.choiceTitle,
                            selected && styles.choiceTitleSelected,
                          ]}>
                          {copy.title}
                        </CCText>
                        <CCText
                          style={[
                            styles.choiceDescription,
                            selected && styles.choiceTextSelected,
                          ]}>
                          {copy.description}
                        </CCText>
                        <CCText
                          style={[
                            styles.moveLabel,
                            selected && styles.choiceTextSelected,
                          ]}>
                          {DIFFICULTY_MOVE_LABELS[difficulty]}
                        </CCText>
                      </Pressable>
                    );
                  })}
                </View>
              </Section>

              <Section
                step="3"
                title="Choose puzzle topics"
                hint={`Optional · select up to ${MAX_TOPICS}`}>
                <Pressable
                  testID="fun-topics-open"
                  onPress={openTopicPicker}
                  style={styles.topicButton}>
                  <View style={styles.topicButtonIcon}>
                    <CCText style={styles.topicButtonIconText}>♟</CCText>
                  </View>
                  <View style={styles.topicButtonCopy}>
                    <CCText style={styles.topicButtonTitle}>
                      {settings.topics.length === 0
                        ? 'Any topic'
                        : topicSummary}
                    </CCText>
                    <CCText style={styles.topicButtonSubtitle}>
                      {settings.topics.length === 0
                        ? 'A surprise mix from every theme'
                        : settings.topics
                            .map(topic => TOPIC_LABELS[topic] ?? topic)
                            .join(' · ')}
                    </CCText>
                  </View>
                  <CCText style={styles.topicButtonArrow}>›</CCText>
                </Pressable>
                <CCText style={styles.fieldHelp}>
                  Tap to browse and search tactical themes.
                </CCText>
              </Section>

              <Section
                step="4"
                title="What happens after mistakes?"
                hint="Keep it friendly or raise the stakes.">
                <View style={styles.ruleGrid}>
                  <Pressable
                    testID="fun-mistakes-friendly"
                    onPress={() => update({punish: false})}
                    style={[
                      styles.ruleCard,
                      !settings.punish && styles.choiceSelected,
                    ]}>
                    <CCText style={styles.ruleIcon}>🤝</CCText>
                    <View style={styles.ruleCopy}>
                      <View style={styles.ruleTitleRow}>
                        <CCText
                          style={[
                            styles.choiceTitle,
                            !settings.punish && styles.choiceTitleSelected,
                          ]}>
                          Keep everyone racing
                        </CCText>
                        <View style={styles.recommendedPill}>
                          <CCText style={styles.recommendedText}>
                            RECOMMENDED
                          </CCText>
                        </View>
                      </View>
                      <CCText
                        style={[
                          styles.choiceDescription,
                          !settings.punish && styles.choiceTextSelected,
                        ]}>
                        Wrong answers reset the streak, but nobody gets knocked
                        out.
                      </CCText>
                    </View>
                  </Pressable>

                  <Pressable
                    testID="fun-mistakes-knockout"
                    onPress={() => update({punish: true})}
                    style={[
                      styles.ruleCard,
                      settings.punish && styles.choiceSelected,
                    ]}>
                    <CCText style={styles.ruleIcon}>💥</CCText>
                    <View style={styles.ruleCopy}>
                      <CCText
                        style={[
                          styles.choiceTitle,
                          settings.punish && styles.choiceTitleSelected,
                        ]}>
                        Knockout mode
                      </CCText>
                      <CCText
                        style={[
                          styles.choiceDescription,
                          settings.punish && styles.choiceTextSelected,
                        ]}>
                        Players leave the scoring race after too many mistakes.
                      </CCText>
                    </View>
                  </Pressable>
                </View>

                {settings.punish ? (
                  <View style={styles.thresholdRow}>
                    <CCText style={styles.thresholdLabel}>
                      Knock a player out after
                    </CCText>
                    <View style={styles.thresholdChoices}>
                      {PUNISH_THRESHOLDS.map(threshold => {
                        const selected =
                          (settings.punishThreshold ?? 5) === threshold;
                        return (
                          <Pressable
                            key={threshold}
                            testID={`fun-threshold-${threshold}`}
                            onPress={() => update({punishThreshold: threshold})}
                            style={[
                              styles.thresholdChip,
                              selected && styles.thresholdChipSelected,
                            ]}>
                            <CCText
                              style={[
                                styles.thresholdText,
                                selected && styles.thresholdTextSelected,
                              ]}>
                              {threshold} mistakes
                            </CCText>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                ) : null}
              </Section>

              <View style={styles.summaryCard}>
                <View style={styles.summaryHeading}>
                  <CCText style={styles.summarySpark}>✦</CCText>
                  <CCText style={styles.summaryTitle}>
                    Your Puzzle Royal is ready
                  </CCText>
                </View>
                <CCText style={styles.summaryText}>
                  {duration} ·{' '}
                  {settings.difficulty[0].toUpperCase() +
                    settings.difficulty.slice(1)}
                  {' · '}
                  {topicSummary}
                </CCText>
                <CCText style={styles.summarySubtext}>{mistakeSummary}</CCText>
              </View>

              {error ? (
                <ErrorBanner
                  testID="fun-challenge-error"
                  message={error}
                  style={styles.error}
                />
              ) : null}
            </ScrollView>

            <View style={styles.footer}>
              <Pressable
                testID="fun-challenge-cancel-btn"
                onPress={close}
                style={styles.cancelButton}>
                <CCText style={styles.cancelText}>Cancel</CCText>
              </Pressable>
              <Pressable
                testID="fun-challenge-create-btn"
                disabled={submitting}
                onPress={submit}
                style={[
                  styles.createButton,
                  submitting && styles.createButtonDisabled,
                ]}>
                <CCText style={styles.createButtonText}>
                  {submitting ? 'Creating challenge…' : 'Create challenge'}
                </CCText>
                {!submitting ? (
                  <CCText style={styles.createArrow}>→</CCText>
                ) : null}
              </Pressable>
            </View>

            <TopicPicker
              visible={topicPickerOpen && !created}
              selected={topicDraft}
              query={topicSearch}
              onQueryChange={setTopicSearch}
              onToggle={toggleTopic}
              onClear={() => setTopicDraft([])}
              onCancel={closeTopicPicker}
              onApply={() => {
                update({topics: topicDraft});
                closeTopicPicker();
              }}
            />

            {created ? (
              <View
                testID="fun-challenge-created"
                style={styles.successOverlay}>
                <View style={styles.successIcon}>
                  <CCText style={{fontSize: 34, lineHeight: 38}}>♛</CCText>
                </View>
                <CCText
                  fontWeight="thunder"
                  style={styles.successTitle}>
                  Puzzle Royal created!
                </CCText>
                <CCText style={styles.successText}>
                  The race is live for {clubName}. Members can join from the
                  Club Room now.
                </CCText>
                <View style={styles.successPill}>
                  <View style={styles.successDot} />
                  <CCText style={styles.successPillText}>
                    Returning to the Club Room…
                  </CCText>
                </View>
              </View>
            ) : null}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function TopicPicker({
  visible,
  selected,
  query,
  onQueryChange,
  onToggle,
  onClear,
  onCancel,
  onApply,
}: {
  visible: boolean;
  selected: string[];
  query: string;
  onQueryChange: (value: string) => void;
  onToggle: (topic: string) => void;
  onClear: () => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  const normalizedQuery = query.trim().toLowerCase();
  const filteredTopics = ROYALE_TOPICS.filter(topic =>
    topic.label.toLowerCase().includes(normalizedQuery),
  );
  const limitReached = selected.length >= MAX_TOPICS;

  if (!visible) {
    return null;
  }

  return (
    <View testID="fun-topics-modal" style={styles.topicOverlay}>
      <View style={styles.topicHeader}>
        <View style={styles.topicHeaderRow}>
          <View>
            <CCText fontWeight="thunder" style={styles.topicTitle}>
              Pick puzzle topics
            </CCText>
            <CCText style={styles.topicHeaderHint}>
              Choose up to {MAX_TOPICS}, or keep a surprise mix.
            </CCText>
          </View>
          <Pressable
            testID="fun-topics-close"
            accessibilityLabel="Close topic picker"
            onPress={onCancel}
            style={styles.topicClose}>
            <CCText style={styles.topicCloseText}>×</CCText>
          </Pressable>
        </View>

        <View style={styles.topicSearchBox}>
          <CCText style={styles.topicSearchIcon}>⌕</CCText>
          <TextInput
            testID="fun-topics-search"
            value={query}
            onChangeText={onQueryChange}
            placeholder="Search forks, pins, attacks…"
            placeholderTextColor="#9B928C"
            autoCapitalize="none"
            autoCorrect={false}
            style={styles.topicSearchInput}
          />
          {query ? (
            <Pressable
              accessibilityLabel="Clear search"
              onPress={() => onQueryChange('')}>
              <CCText style={styles.topicSearchClear}>×</CCText>
            </Pressable>
          ) : null}
        </View>

        <View style={styles.topicSelectionStatus}>
          <CCText
            style={[styles.topicCount, limitReached && styles.topicCountFull]}>
            {selected.length} of {MAX_TOPICS} selected
          </CCText>
          {selected.length > 0 ? (
            <Pressable testID="fun-topics-clear" onPress={onClear}>
              <CCText style={styles.topicClearText}>Clear all</CCText>
            </Pressable>
          ) : (
            <CCText style={styles.topicAnyText}>Any topic is selected</CCText>
          )}
        </View>
      </View>

      <ScrollView
        testID="fun-topics-list"
        style={styles.topicList}
        contentContainerStyle={styles.topicListContent}
        keyboardShouldPersistTaps="handled"
        nestedScrollEnabled
        showsVerticalScrollIndicator>
        {filteredTopics.length > 0 ? (
          filteredTopics.map(topic => {
            const checked = selected.includes(topic.key);
            const disabled = limitReached && !checked;
            return (
              <Pressable
                key={topic.key}
                testID={`fun-topic-${topic.key}`}
                disabled={disabled}
                onPress={() => onToggle(topic.key)}
                style={[
                  styles.topicOption,
                  checked && styles.topicOptionSelected,
                  disabled && styles.topicOptionDisabled,
                ]}>
                <View
                  style={[
                    styles.topicCheckbox,
                    checked && styles.topicCheckboxSelected,
                  ]}>
                  {checked ? (
                    <CCText style={styles.topicCheckboxText}>✓</CCText>
                  ) : null}
                </View>
                <CCText
                  style={[
                    styles.topicOptionText,
                    checked && styles.topicOptionTextSelected,
                  ]}>
                  {topic.label}
                </CCText>
              </Pressable>
            );
          })
        ) : (
          <View style={styles.topicEmpty}>
            <CCText style={styles.topicEmptyIcon}>♟</CCText>
            <CCText style={styles.topicEmptyTitle}>No topic found</CCText>
            <CCText style={styles.topicEmptyText}>
              Try a simpler search, such as “attack” or “pawn”.
            </CCText>
          </View>
        )}
      </ScrollView>

      <View style={styles.topicFooter}>
        <Pressable onPress={onCancel} style={styles.topicCancelButton}>
          <CCText style={styles.topicCancelText}>Cancel</CCText>
        </Pressable>
        <Pressable
          testID="fun-topics-apply"
          onPress={onApply}
          style={styles.topicApplyButton}>
          <CCText style={styles.topicApplyText}>
            {selected.length === 0
              ? 'Use any topic'
              : `Apply ${selected.length} topic${
                  selected.length === 1 ? '' : 's'
                }`}
          </CCText>
        </Pressable>
      </View>
    </View>
  );
}

function Section({
  step,
  title,
  hint,
  children,
}: {
  step: string;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeading}>
        <View style={styles.stepBadge}>
          <CCText style={styles.stepText}>{step}</CCText>
        </View>
        <View style={styles.sectionHeadingCopy}>
          <CCText style={styles.sectionTitle}>{title}</CCText>
          <CCText style={styles.sectionHint}>{hint}</CCText>
        </View>
      </View>
      {children}
    </View>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(12,20,18,0.58)',
  },
  bottom: {
    justifyContent: 'flex-end',
  },
  center: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 28,
  },
  sheet: {
    position: 'relative',
    backgroundColor: Colors.primaryLight,
    width: '100%',
    maxHeight: '94%',
    overflow: 'hidden',
  },
  bottomSheet: {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    height: '92%',
  },
  card: {
    borderRadius: 28,
    maxWidth: 560,
    height: '90%',
  },
  grabber: {
    position: 'absolute',
    zIndex: 2,
    top: 9,
    width: 42,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.38)',
    alignSelf: 'center',
  },
  hero: {
    backgroundColor: Colors.primaryDark,
    paddingHorizontal: 22,
    paddingTop: 20,
    paddingBottom: 22,
  },
  heroTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  heroIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.primaryAction,
    transform: [{rotate: '-4deg'}],
  },
  heroIconText: {
    color: '#3E3425',
    fontSize: 22,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  closeButtonText: {
    color: '#E8F2EF',
    fontSize: 25,
    lineHeight: 27,
  },
  title: {
    color: '#FFF9E9',
    fontSize: 27,
    lineHeight: 31,
  },
  titleMobile: {
    fontSize: 33,
    lineHeight: 37,
  },
  subtitle: {
    maxWidth: 470,
    color: '#C9DBD6',
    fontSize: 13.5,
    lineHeight: 19,
    marginTop: 6,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  content: {
    padding: 20,
    paddingBottom: 12,
  },
  infoStrip: {
    flexDirection: 'row',
    gap: 11,
    padding: 13,
    borderRadius: 14,
    backgroundColor: '#EEF7F4',
    borderWidth: 1,
    borderColor: '#D5EBE5',
    marginBottom: 23,
  },
  infoIcon: {
    fontSize: 20,
  },
  infoCopy: {
    flex: 1,
  },
  infoTitle: {
    color: '#245C54',
    fontSize: 12.5,
    fontWeight: '700',
    marginBottom: 2,
  },
  infoText: {
    color: '#55726C',
    fontSize: 11.5,
    lineHeight: 16,
  },
  section: {
    marginBottom: 26,
  },
  sectionHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
  },
  stepBadge: {
    width: 27,
    height: 27,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E5F2EF',
  },
  stepText: {
    color: '#26766C',
    fontSize: 12,
    fontWeight: '800',
  },
  sectionHeadingCopy: {
    flex: 1,
  },
  sectionTitle: {
    color: '#2C2927',
    fontSize: 14.5,
    fontWeight: '700',
  },
  sectionHint: {
    color: '#8B827C',
    fontSize: 11.5,
    marginTop: 1,
  },
  durationGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  durationCard: {
    position: 'relative',
    width: '31.7%',
    minWidth: 100,
    paddingHorizontal: 12,
    paddingVertical: 11,
    borderRadius: 13,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E1DDD5',
  },
  durationName: {
    color: '#383431',
    fontSize: 12.5,
    fontWeight: '700',
  },
  durationTime: {
    color: '#8A817B',
    fontSize: 11,
    marginTop: 2,
  },
  checkBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 17,
    height: 17,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F4D36B',
  },
  checkText: {
    color: '#284C46',
    fontSize: 10,
    fontWeight: '900',
  },
  difficultyGrid: {
    flexDirection: 'row',
    gap: 8,
  },
  difficultyCard: {
    flex: 1,
    minWidth: 0,
    padding: 12,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E1DDD5',
  },
  difficultyIcon: {
    fontSize: 19,
    marginBottom: 7,
  },
  choiceSelected: {
    backgroundColor: Colors.secondaryAction,
    borderColor: Colors.secondaryAction,
  },
  choiceTitle: {
    color: '#383431',
    fontSize: 12.5,
    fontWeight: '700',
  },
  choiceTitleSelected: {
    color: '#FFFFFF',
  },
  choiceDescription: {
    color: '#817872',
    fontSize: 10.5,
    lineHeight: 14,
    marginTop: 3,
  },
  choiceTextSelected: {
    color: '#CAE0DA',
  },
  moveLabel: {
    color: '#397A70',
    fontSize: 10,
    fontWeight: '700',
    marginTop: 7,
  },
  topicButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    minHeight: 66,
    padding: 12,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DCD7CE',
  },
  topicButtonIcon: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E6F2EF',
  },
  topicButtonIconText: {
    color: '#2D7067',
    fontSize: 21,
  },
  topicButtonCopy: {
    flex: 1,
    minWidth: 0,
  },
  topicButtonTitle: {
    color: '#35312E',
    fontSize: 13,
    fontWeight: '700',
  },
  topicButtonSubtitle: {
    color: '#877E78',
    fontSize: 11,
    lineHeight: 15,
    marginTop: 3,
  },
  topicButtonArrow: {
    color: '#4B766F',
    fontSize: 27,
    lineHeight: 28,
  },
  fieldHelp: {
    color: '#918883',
    fontSize: 11,
    marginTop: 7,
  },
  ruleGrid: {
    gap: 8,
  },
  ruleCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    padding: 13,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E1DDD5',
  },
  ruleIcon: {
    fontSize: 19,
  },
  ruleCopy: {
    flex: 1,
  },
  ruleTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 7,
  },
  recommendedPill: {
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: '#F5D876',
  },
  recommendedText: {
    color: '#5B4710',
    fontSize: 7.5,
    fontWeight: '900',
    letterSpacing: 0.4,
  },
  thresholdRow: {
    marginTop: 11,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#F0ECE3',
  },
  thresholdLabel: {
    color: '#655B54',
    fontSize: 11.5,
    fontWeight: '600',
    marginBottom: 8,
  },
  thresholdChoices: {
    flexDirection: 'row',
    gap: 8,
  },
  thresholdChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#DDD6CC',
  },
  thresholdChipSelected: {
    backgroundColor: '#D96A42',
    borderColor: '#D96A42',
  },
  thresholdText: {
    color: '#655B54',
    fontSize: 11.5,
    fontWeight: '600',
  },
  thresholdTextSelected: {
    color: '#FFFFFF',
  },
  summaryCard: {
    padding: 15,
    borderRadius: 15,
    backgroundColor: 'rgba(250,207,71,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(250,207,71,0.48)',
    marginBottom: 8,
  },
  summaryHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    marginBottom: 5,
  },
  summarySpark: {
    color: '#8B6910',
    fontSize: 16,
  },
  summaryTitle: {
    color: '#4D3C14',
    fontSize: 13,
    fontWeight: '800',
  },
  summaryText: {
    color: '#5E4C24',
    fontSize: 12,
    fontWeight: '600',
  },
  summarySubtext: {
    color: '#876F38',
    fontSize: 11,
    marginTop: 3,
  },
  error: {
    marginTop: 12,
  },
  footer: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 13,
    paddingBottom: 20,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E8E2D8',
  },
  cancelButton: {
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    color: '#5E554F',
    fontSize: 14,
    fontWeight: '600',
  },
  createButton: {
    flex: 1,
    minHeight: 47,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    backgroundColor: Colors.secondaryAction,
  },
  createButtonDisabled: {
    backgroundColor: '#A8CEC8',
  },
  createButtonText: {
    color: '#FFFFFF',
    fontSize: 14.5,
    fontWeight: '700',
  },
  createArrow: {
    color: '#FFFFFF',
    fontSize: 18,
  },
  topicOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 20,
    backgroundColor: Colors.primaryLight,
  },
  topicHeader: {
    paddingHorizontal: 20,
    paddingTop: 22,
    paddingBottom: 12,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: '#E8E2D8',
  },
  topicHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 14,
  },
  topicTitle: {
    color: '#2B2927',
    fontSize: 23,
  },
  topicHeaderHint: {
    color: '#807771',
    fontSize: 11.5,
    marginTop: 3,
  },
  topicClose: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F0ECE5',
  },
  topicCloseText: {
    color: '#625A54',
    fontSize: 24,
    lineHeight: 26,
  },
  topicSearchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 16,
    minHeight: 46,
    paddingHorizontal: 13,
    borderRadius: 13,
    backgroundColor: '#F5F2EC',
    borderWidth: 1,
    borderColor: '#E2DDD4',
  },
  topicSearchIcon: {
    color: '#47766E',
    fontSize: 22,
  },
  topicSearchInput: {
    flex: 1,
    paddingVertical: 10,
    color: '#302D2B',
    fontSize: 13.5,
  },
  topicSearchClear: {
    color: '#8B827C',
    fontSize: 21,
  },
  topicSelectionStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 11,
  },
  topicCount: {
    color: '#34746B',
    fontSize: 11.5,
    fontWeight: '700',
  },
  topicCountFull: {
    color: '#B85D3C',
  },
  topicClearText: {
    color: '#357C72',
    fontSize: 11.5,
    fontWeight: '700',
  },
  topicAnyText: {
    color: '#968C85',
    fontSize: 11,
  },
  topicList: {
    flex: 1,
    minHeight: 0,
  },
  topicListContent: {
    padding: 16,
    gap: 7,
  },
  topicOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    minHeight: 48,
    paddingHorizontal: 13,
    paddingVertical: 10,
    borderRadius: 13,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E3DED6',
  },
  topicOptionSelected: {
    backgroundColor: '#E9F5F2',
    borderColor: '#70B7AC',
  },
  topicOptionDisabled: {
    opacity: 0.42,
  },
  topicCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 7,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#C5BDB5',
    backgroundColor: '#FFFFFF',
  },
  topicCheckboxSelected: {
    backgroundColor: Colors.secondaryAction,
    borderColor: Colors.secondaryAction,
  },
  topicCheckboxText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '900',
  },
  topicOptionText: {
    flex: 1,
    color: '#4A4541',
    fontSize: 13,
    fontWeight: '600',
  },
  topicOptionTextSelected: {
    color: '#245F57',
    fontWeight: '700',
  },
  topicEmpty: {
    alignItems: 'center',
    paddingVertical: 44,
    paddingHorizontal: 20,
  },
  topicEmptyIcon: {
    color: '#91A8A3',
    fontSize: 30,
    marginBottom: 8,
  },
  topicEmptyTitle: {
    color: '#4D4844',
    fontSize: 14,
    fontWeight: '700',
  },
  topicEmptyText: {
    color: '#918883',
    fontSize: 11.5,
    lineHeight: 16,
    textAlign: 'center',
    marginTop: 4,
  },
  topicFooter: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 20,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E8E2D8',
  },
  topicCancelButton: {
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topicCancelText: {
    color: '#655D57',
    fontSize: 14,
    fontWeight: '600',
  },
  topicApplyButton: {
    flex: 1,
    minHeight: 46,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 13,
    borderRadius: 999,
    backgroundColor: Colors.secondaryAction,
  },
  topicApplyText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  successOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 50,
    borderRadius: 18,
    backgroundColor: '#F4FBF9',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 28,
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DDF3EE',
    borderWidth: 1,
    borderColor: 'rgba(61,171,158,0.35)',
    marginBottom: 18,
  },
  successTitle: {
    color: '#262322',
    fontSize: 30,
    lineHeight: 34,
    textAlign: 'center',
  },
  successText: {
    color: '#6B625E',
    fontSize: 13.5,
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 340,
    marginTop: 8,
  },
  successPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    borderRadius: 999,
    backgroundColor: '#E7F5F2',
    paddingHorizontal: 13,
    paddingVertical: 8,
    marginTop: 20,
  },
  successDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#3DAB9E',
  },
  successPillText: {
    color: '#2C756D',
    fontSize: 11.5,
    fontWeight: '700',
  },
});
